import React from 'react';

import styled from 'styled-components';

import { isNonNegativeNumber } from 'utils/helpers/numbers';

import {
  FEES_FIELD_COMMUNITY_FEE,
  FEES_FIELDS,
  FIELD_CATEGORY,
  LEVEL_FEES_FIELDS_LABELS,
} from '../../../ShopData/constants';
import Header from '../../common/header/Header';
import { useCareTypesContext } from '../CareTypesContext';
import FeesItems from '../common/FeesItems';
import { CCRC_FEES_CONFIGURATIONS, FREQUENCY_OPTIONS, RENTAL_FEES_CONFIGURATIONS } from '../constants';
import { CommunityFeesType } from '../types';

const Container = styled.div`
  box-sizing: border-box;
  position: relative;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

type Props = {
  fee?: CommunityFeesType;
  isCcrc: boolean;
}

const CommunityFeePreview = ({ fee, isCcrc }: Props) => {
  const { contract } = useCareTypesContext();
  const depositEquivalent = FREQUENCY_OPTIONS.find((item) => item.value == fee?.deposit_equivalent_factor)?.label;
  const showCommunityFee =
    isNonNegativeNumber(fee?.community_fee) || isNonNegativeNumber(fee?.frequency);
  const showDeposit = isNonNegativeNumber(fee?.deposit) || isNonNegativeNumber(depositEquivalent);
  const deposit = !depositEquivalent ? fee?.deposit : undefined;

  const predefinedCommunityFeeEquivalentLabel = FREQUENCY_OPTIONS.find((item) => item.value == fee?.frequency)?.label;
  const communityFeeEquivalentLabel =
    predefinedCommunityFeeEquivalentLabel ?? `Equal to ${(fee?.frequency || 0) * 100}% of month's rent`;

  if (!fee) {
    return null;
  }

  return (
    <Container>
      <Header title="Community Fees" />
      <Content>
        {showCommunityFee && (
          <FeesItems
            history={{
              category: FIELD_CATEGORY.FEES_INCENTIVES,
              field: FEES_FIELD_COMMUNITY_FEE,
              label: LEVEL_FEES_FIELDS_LABELS[FEES_FIELD_COMMUNITY_FEE],
              community_contract_id: contract?.id,
            }}
            field={FEES_FIELD_COMMUNITY_FEE}
            title="Community Fee"
            amount={fee?.frequency ? fee?.community_fee_equivalent : fee?.community_fee}
            subtitle={fee?.frequency ? communityFeeEquivalentLabel : undefined}
          />
        )}
        {isCcrc && (
          CCRC_FEES_CONFIGURATIONS.map(
            ({ title, key, subtitle }, index) =>
              isNonNegativeNumber(fee[key]) && (
                <FeesItems
                  key={index}
                  history={{
                    category: FIELD_CATEGORY.FEES_INCENTIVES,
                    field: key,
                    label: LEVEL_FEES_FIELDS_LABELS[key],
                    community_contract_id: contract?.id,
                  }}
                  field={key}
                  title={title}
                  amount={fee[key] as number}
                  subtitle={subtitle || ''}
                />
              )
          )
        )}
        {RENTAL_FEES_CONFIGURATIONS.map(
          ({ title, key, subtitle }, index) =>
            isNonNegativeNumber(fee[key]) && (
              <FeesItems
                key={index}
                history={{
                  category: FIELD_CATEGORY.FEES_INCENTIVES,
                  field: key,
                  label: LEVEL_FEES_FIELDS_LABELS[key],
                  community_contract_id: contract?.id,
                }}
                field={key}
                title={title}
                amount={fee[key] as number}
                subtitle={subtitle || ''}
              />
            )
        )}
        {showDeposit && (
          <FeesItems
            history={{
              category: FIELD_CATEGORY.FEES_INCENTIVES,
              field: FEES_FIELDS.DEPOSIT,
              label: LEVEL_FEES_FIELDS_LABELS[FEES_FIELDS.DEPOSIT],
              community_contract_id: contract?.id,
            }}
            field={FEES_FIELDS.DEPOSIT}
            title="Deposit Fee"
            amount={deposit}
            subtitle={depositEquivalent}
          />
        )}
      </Content>
    </Container>
  );
};

export default CommunityFeePreview;
